package nl.astraeus.midi.view

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.canvas
import kotlinx.html.style
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.komp.currentElement
import nl.astraeus.midi.Note
import nl.astraeus.midi.audio.AudioContextHandler
import nl.astraeus.midi.audio.VstChipWorklet
import nl.astraeus.midi.message.TimedMidiMessage
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HIGH
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.ImageSmoothingQuality
import kotlin.js.Date
import kotlin.random.Random

class PracticeStaff(
  var channel: Int = -1,
  val trebble: Boolean = true
) : Komponent() {
  val random = Random(Date.now().toLong())
  var symbols: Symbols = Symbols()
  var canvas = document.createElement("canvas") as HTMLCanvasElement
  var ctx = canvas.getContext("2d") as CanvasRenderingContext2D
  var playingNotes = mutableSetOf<Note>()

  var notes = Array(0) { Note.C4 }
  var nextNote = 0
  var errors = 0
  var scrollX = 0.0
  var startTime: Double? = null
  var endTime: Double? = null

  init {
    reset()
  }

  private fun reset() {
    notes = Array(50) {
      var note = 0
      do {
        if (trebble) {
          note = Note.C4.ordinal + random.nextInt(25)
        } else {
          note = Note.C2.ordinal + random.nextInt(25)
        }
      } while (Note.entries[note].blackKey || Note.entries[note] == Note.NONE)
      Note.entries[note]
    }
    nextNote = 0
    errors = 0
    scrollX = 0.0
    startTime = null
    endTime = null
  }

  fun handleMidi(data: Uint8Array) {
    if (channel == -1 || data[0].toInt() and channel == channel) {
      when (data.get(0).toInt() and 0xf0) {
        0x90 -> {
          val note = Note.entries[data[1].toInt()]

          playingNotes.add(note)
          VstChipWorklet.postMessage(
            TimedMidiMessage(
              AudioContextHandler.audioContext.currentTime,
              0x90.toByte(), data[1], data[2]
            ).data.buffer.toByteArray()
          )

          if (endTime != null) {
            val delta = (Date.now() - (endTime ?: 0.0)) / 1000.0
            if (delta > 1.0) {
              reset()
            }
          } else {
            if (nextNote < notes.size) {
              if (notes[nextNote] == note) {
                nextNote++

                if (startTime == null) {
                  startTime = Date.now()
                }
              } else if (startTime != null) {
                errors++
              }
            }

            if (nextNote == notes.size && endTime == null) {
              endTime = Date.now()
            }
          }
        }

        0x80 -> {
          val note = Note.entries[data[1].toInt()]

          playingNotes.remove(note)
          VstChipWorklet.postMessage(
            TimedMidiMessage(
              AudioContextHandler.audioContext.currentTime,
              0x80.toByte(), data[1], 0
            ).data.buffer.toByteArray()
          )
        }
      }
    }
  }

  override fun HtmlBuilder.render() {
    canvas {
      width = "2100"
      height = "500"
      style = "width: 100%;"

      this@PracticeStaff.canvas = currentElement() as HTMLCanvasElement
      this@PracticeStaff.ctx = canvas.getContext("2d") as CanvasRenderingContext2D

      draw()
    }
  }

  override fun renderUpdate() {
  }

  fun draw() {
    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = ImageSmoothingQuality.HIGH  // LOW, MEDIUM, or HIGH

    ctx.font = "40px Bravura"
    ctx.fillStyle = "black"
    ctx.strokeStyle = "black"

    ctx.fillText("Done: ${nextNote}/${notes.size}", 200.5, 40.5)
    val st = startTime
    val et = endTime
    val seconds = if (st == null) {
      "-"
    } else if (et == null) {
      ((Date.now() - st) / 1000f).toInt().toString()
    } else {
      ((et - st) / 1000f).toString()
    }

    ctx.fillText("Time: $seconds seconds", 800.5, 40.5)

    val size = ctx.measureText("Errors: $errors")
    ctx.fillText("Errors: $errors", 1900.5 - size.width, 40.5)

    ctx.beginPath()
    ctx.strokeStyle = "black"
    ctx.lineWidth = 2.0

    for (y in 0..<5) {
      ctx.moveTo(200.5, 200.5 + y * 25.0)
      ctx.lineTo(1900.5, 200.5 + y * 25.0)
    }

    ctx.moveTo(200.5, 200.5)
    ctx.lineTo(200.5, 300.5)
    ctx.moveTo(1900.5, 200.5)
    ctx.lineTo(1900.5, 300.5)

    ctx.stroke()

    if (trebble) {
      ctx.drawSymbol(
        symbols.trebleClef,
        230.5, 275.5
      )
    } else {
      ctx.drawSymbol(
        symbols.bassClef,
        230.5, 225.5
      )
    }

    /*
        ctx.drawSymbol(
          symbols.time4,
          300.5, 225.5
        )
        ctx.drawSymbol(
          symbols.time4,
          300.5, 275.5
        )
    */

    var x = 400.0 - scrollX

    for (note in notes) {
      ctx.beginPath()
      if (x > 299.0 && x < 1800.0) {
        if (x < 400) {
          ctx.globalAlpha = (x - 300.0) / 100.0
        } else if (x > 1700) {
          ctx.globalAlpha = (1800 - x) / 100.0
        } else {
          ctx.globalAlpha = 1.0
        }
        if (trebble) {
          if (note.ordinal >= Note.A5.ordinal) {
            ctx.moveTo(x + 15.0, 175.5)
            ctx.lineTo(x + 60.0, 175.5)
          }
          if (note.ordinal >= Note.C6.ordinal) {
            ctx.moveTo(x + 15.0, 150.5)
            ctx.lineTo(x + 60.0, 150.5)
          }
          if (note.ordinal >= Note.E6.ordinal) {
            ctx.moveTo(x + 15.0, 125.5)
            ctx.lineTo(x + 60.0, 125.5)
          }

          if (note.ordinal <= Note.C4.ordinal) {
            ctx.moveTo(x + 15.0, 325.5)
            ctx.lineTo(x + 60.0, 325.5)
          }
          if (note.ordinal <= Note.A3.ordinal) {
            ctx.moveTo(x + 15.0, 350.5)
            ctx.lineTo(x + 60.0, 350.5)
          }
          if (note.ordinal <= Note.F3.ordinal) {
            ctx.moveTo(x + 15.0, 375.5)
            ctx.lineTo(x + 60.0, 375.5)
          }
        } else {
          if (note.ordinal >= Note.C4.ordinal) {
            ctx.moveTo(x + 15.0, 175.5)
            ctx.lineTo(x + 60.0, 175.5)
          }
          if (note.ordinal >= Note.E4.ordinal) {
            ctx.moveTo(x + 15.0, 150.5)
            ctx.lineTo(x + 60.0, 150.5)
          }
          if (note.ordinal >= Note.G4.ordinal) {
            ctx.moveTo(x + 15.0, 125.5)
            ctx.lineTo(x + 60.0, 125.5)
          }

          if (note.ordinal <= Note.E2.ordinal) {
            ctx.moveTo(x + 15.0, 325.5)
            ctx.lineTo(x + 60.0, 325.5)
          }
          if (note.ordinal <= Note.C2.ordinal) {
            ctx.moveTo(x + 15.0, 350.5)
            ctx.lineTo(x + 60.0, 350.5)
          }
          if (note.ordinal <= Note.A1.ordinal) {
            ctx.moveTo(x + 15.0, 375.5)
            ctx.lineTo(x + 60.0, 375.5)
          }
        }

        drawNote(x, note)
      }
      x += 100.0
      ctx.stroke()
    }

    ctx.globalAlpha = 0.2

    for (note in playingNotes) {
      drawNote(300.0, note)
    }

    ctx.fillStyle = "black"
    ctx.fillRect(412.5, 100.0, 50.0, 300.0)
    ctx.globalAlpha = 1.0

    ctx.stroke()
    ctx.closePath()

    if (scrollX < nextNote * 100.0 - 1.0) {
      scrollX += (nextNote * 100.0 - scrollX) / 20.0
    }

    if (et != null) {
      ctx.beginPath()
      val delta = (Date.now() - et) / 1000.0
      if (delta < 1.0) {
        ctx.globalAlpha = delta
      } else {
        ctx.globalAlpha = 1.0
      }

      val txt1 = "Played ${notes.size} notes in $seconds seconds with $errors errors!"
      val txt2 = "Press any keyboard key to try again."
      val size1 = ctx.measureText(txt1)
      val size2 = ctx.measureText(txt2)
      ctx.fillText(txt1, 1000.5 - size1.width / 2.0, 350.5)
      ctx.fillText(txt2, 1000.5 - size2.width / 2.0, 400.5)

      ctx.globalAlpha = 1.0
      ctx.beginPath()
    }

    window.requestAnimationFrame {
      draw()
    }
  }

  private fun drawNote(x: Double, note: Note) {
    val symbol = if (trebble) {
      if (note.ordinal > Note.C5.ordinal) {
        symbols.quarterNoteDown
      } else {
        symbols.quarterNoteUp
      }
    } else {
      if (note.ordinal > Note.E3.ordinal) {
        symbols.quarterNoteDown
      } else {
        symbols.quarterNoteUp
      }
    }

    val y = if (trebble) {
      note.trebblePosSharp
    } else {
      note.bassPosSharp
    }

    if (note.blackKey) {
      ctx.drawSymbol(
        symbols.sharp,
        x, y
      )
    }
    ctx.drawSymbol(
      symbol,
      x + 25.0, y
    )
  }
}